/**
 * Доля хвоста публичного рендерера: вид `loader_processing` (экран загрузки
 * перед пейволлом: стадии, отзывы, модалки-вопросы, автопереход).
 *
 * WEB-2920, заход 1: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово. Метка `case 'loader_processing'` и хуки фасада остаются в фасаде; ветка
 * вида — функция отрисовки `renderLoaderProcessing(ctx)`, замыкания фасада
 * приходят объектом-контекстом (`ExtendedRenderContext`). Эту долю импортирует
 * только фасад: она едет в ленивом куске `extended`, а не в первом экране
 * (самопроверка обхода).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePageShown } from '@/public/useAppShownSignal';
import {
  badgeInnerBackgroundStyle,
  toBlockBackground,
} from '../blockBackgroundStyle';
import type { BlockBackground } from '../blockBackgroundStyle';
// WEB-1699 п.2: авто-контраст текста по заданной заливке — тот же приём, что у
// кнопок и плашек (класс WEB-966), второй копии правила не заводим.
import { pickOnPrimaryColor } from '@/themes/colorUtils';
// WEB-1699 (второй возврат 02.09): гейт непрозрачности заливки — из
// существующего помощника, третий свой порог не заводим (WEB-1859 сводит два
// уже разъехавшихся). WEB-1711 (возврат 02.09): зовётся общий порог НАПРЯМУЮ,
// а не через `opaqueHexOf` — тот с WEB-1859 стал чистым переходником к нему
// (captionChipAppearance.ts:77), и лишний прыжок только прятал от читателя, чей
// это порог. Поведение то же до байта.
import { opaqueContrastBaseOf } from '@/themes/surfaceOpacity';
import type { LoaderStep, LoaderQuestion } from '@/store/editorSlice';
// WEB-925 (гэп 1): ключ ответа модалки лоадера — один на рендер и на сборщик
// аналитики, чтобы ответы не разъехались (шрам WEB-923).
import {
  loaderQuestionValueKey,
  isLoaderQuestionValueKey,
  isAnswerBlockType,
  pickerUnitValueKey,
  isPickerUnitValueKey,
} from '@/components/quiz-runtime/answerCollection';
// WEB-1012: экран загрузки берёт стадии и отзывы из самой воронки — окружение
// приходит контекстом, потому что блок видит только себя.
import { useFunnelContent } from '@/components/quiz-runtime/FunnelContentContext';
import { resolveQuizLoader } from '@/components/quiz-runtime/quizLoader';
import {
  funnelSectionStages,
  funnelReviewQuotes,
} from '@/components/quiz-runtime/funnelLoaderSource';
import { getStylePackById } from '@/themes/stylePacks';
import type { StylePackLoader } from '@/themes/stylePack.types';
import type { VariableContext } from '@/player/variable-context';
import { RichTextContent } from '../RichTextContent';
import { asRichField } from '@/types/rich-text';
import type { RichField } from '@/types/rich-text';
import { useFormValues } from '../FormValuesContext';
import {
  blockTextRoles,
  mutedTextRoleStyle,
  scaledTextRoleStyle,
  type TextRoleStyle,
} from '../semanticTextRoles';
import { useEditorPreview } from '../EditorPreviewContext';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { pickStrings } from '@/i18n';
import { authorHintsStrings } from '@/i18n/dictionaries/authorHints';
import { aiFields } from '../aiShapedFields';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';

/**
 * WEB-447 P1-2b — loader_processing: "psychological" pre-paywall loader.
 * Steps complete sequentially according to their durationMs; a completed
 * step gets a check mark, the active one shows a spinning ring.
 */
// WEB-785: default title/steps used to be hardcoded English constants here;
// now resolved per-render via getPublicQuizStrings(varCtx?.system?.locale)
// (funnel language) inside LoaderProcessingBlock, with 'en' text unchanged
// when no locale is available (e.g. editor canvas preview).
const LOADER_ACCENT = 'var(--color-primary, #4094F7)';
/** Дорожка кольца и полосы по умолчанию (своё поле trackColor перебивает). */
const LOADER_TRACK = 'var(--color-bg-tertiary, #f0f0f0)';
/** Кольцо по умолчанию: 112 px, толщина 8, радиус 46 (поля ringSize/ringStroke). */
const LOADER_RING_SIZE = 112;
const LOADER_RING_STROKE = 8;
// Контур галочки SVG (команды M/L и координаты), не текст покупателю.
const LOADER_CHECK_PATH = 'M2 6.2 L4.8 9 L10 3.4'; // i18n-allow: SVG-путь галочки, не надпись

function LoaderCheckIcon(): React.ReactElement {
  return (
    <svg
      viewBox="0 0 12 12"
      width={11}
      height={11}
      fill="none"
      aria-hidden="true"
    >
      <path
        d={LOADER_CHECK_PATH}
        stroke="var(--color-on-primary, #fff)"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * WEB-925: дефолт подсекции `loader` для воронок без пака и для выдуманного
 * id пака. Значения ровно воспроизводят сегодняшний вид лоадера — полоса плюс
 * все стадии сразу, без процента, соцпруфа и сводки ответов.
 */
const DEFAULT_LOADER_STYLE: StylePackLoader = {
  style: 'all-stages',
  showPercent: false,
  // WEB-1012 (решение владельца 11.08): карусель отзывов и вопросы-модалки
  // включены ВСЕМ — и воронкам без пака тоже. Отключение — настройкой самого
  // блока (showSocialProof/showEngagementModals в панели свойств).
  socialProof: 'review',
  answersSummary: false,
  engagementModals: true,
};

/**
 * WEB-925: человекочитаемая сводка ответов для стиля Astroline. Значения берём
 * из контекста переменных: сырые id опций подменяются их подписями через
 * blockOptions, иначе на карточках оказались бы служебные строки.
 */
function loaderAnswerSummaryItems(
  varCtx?: VariableContext,
  limit = 4,
  selfBlockId?: string,
): string[] {
  if (!varCtx?.answers) return [];
  const items: string[] = [];
  Object.entries(varCtx.answers).forEach(([blockId, raw]) => {
    // WEB-1011: в сводку идут ТОЛЬКО ответы блоков-вопросов. В values лежат и
    // служебные записи — маркер завершения лоадера 'done' (WEB-682b) и ответы
    // его модалок; без фильтра они выпадали карточками на живой экран, из-за
    // чего dark-mystic печатал покупателю английское слово «done».
    if (blockId === selfBlockId) return;
    if (isLoaderQuestionValueKey(blockId)) return;
    // WEB-1301: companion-ключ единицы измерения (pickerUnitValueKey) не блок
    // и не сам по себе ответ — без фильтра "kg"/"lb"/"cm"/"ft" всплыли бы
    // отдельной служебной карточкой (тот же класс, что и loader-question-ключ
    // выше и маркер 'done', WEB-1011): meta для него не существует, а
    // фолбэк "нет описания — считаем ответом" ниже иначе бы его пропустил.
    if (isPickerUnitValueKey(blockId)) return;
    const meta = varCtx.blockMeta?.[blockId];
    // Ключ без описания блока считаем ответом: плеер описывает каждый блок
    // экрана, поэтому неизвестные ключи бывают только у ручных контекстов —
    // терять на них настоящие ответы хуже, чем пропустить чужую запись.
    if (meta && !isAnswerBlockType(meta.type)) return;
    const options = varCtx.blockOptions?.[blockId] ?? [];
    const values = Array.isArray(raw) ? raw : [raw];
    const isPickerType =
      meta?.type === 'weight_picker' || meta?.type === 'height_picker';
    // WEB-1301 (Осторожно #4): голое число без единицы — «непонятные данные».
    // Единица берётся из факта переключения посетителем (companion-ключ),
    // иначе из дефолта блока (meta.unitDefault).
    const pickerUnit = isPickerType
      ? ((varCtx.answers[pickerUnitValueKey(blockId)] as string | undefined) ??
        meta?.unitDefault)
      : undefined;
    values.forEach((value) => {
      let text = options.find((opt) => opt.id === value)?.label ?? value;
      if (
        isPickerType &&
        pickerUnit &&
        typeof text === 'string' &&
        text.trim() !== ''
      ) {
        text = `${text} ${pickerUnit}`;
      }
      if (typeof text === 'string' && text.trim() !== '') items.push(text);
    });
  });
  return items.slice(0, limit);
}

/** WEB-925: кольцо-процент (BetterMe №2 / Clario) вместо линейной полосы. */
/** Размер цифры процента в кольце лоадера (WEB-925). */
const LOADER_RING_PERCENT_SIZE = 26;

function LoaderRing({
  percent,
  accent,
  track = LOADER_TRACK,
  size = LOADER_RING_SIZE,
  stroke = LOADER_RING_STROKE,
  showPercent = true,
  gapBelow = 20,
}: {
  percent: number;
  accent: string;
  /** Отступ под кольцом, px (подпись под кольцом — 16, как над ним). */
  gapBelow?: number;
  /** Слово владельца 21.10.2026: свои поля settings.quizLoader поверх пака. */
  track?: string;
  size?: number;
  stroke?: number;
  showPercent?: boolean;
}): React.ReactElement {
  // Поле от кольца до рамки растёт с размером: при 112/8 — прежний радиус 46.
  const scale = size / LOADER_RING_SIZE;
  const c = size / 2;
  const radius = Math.max(1, c - stroke / 2 - 6 * scale);
  const circumference = 2 * Math.PI * radius;
  return (
    <div
      data-testid="loader-ring"
      style={{
        position: 'relative',
        width: size,
        height: size,
        margin: `0 auto ${gapBelow}px`,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
      >
        <circle
          cx={c}
          cy={c}
          r={radius}
          fill="none"
          stroke={track}
          strokeWidth={stroke}
        />
        <circle
          cx={c}
          cy={c}
          r={radius}
          fill="none"
          stroke={accent}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - percent / 100)}
          transform={`rotate(-90 ${c} ${c})`}
          style={{ transition: 'stroke-dashoffset 0.4s ease' }}
        />
      </svg>
      {showPercent && (
        <span
          data-testid="loader-percent"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Литерал вынесен в константу намеренно: гвард hero-типографики
            // (WEB-447) ищет плоский размер шрифта прямо в исходнике этого файла
            // и срабатывал на проценте кольца лоадера, к заголовку hero
            // отношения не имеющем.
            fontSize: Math.round(LOADER_RING_PERCENT_SIZE * scale),
            fontWeight: 700,
          }}
        >
          {percent}%
        </span>
      )}
    </div>
  );
}

interface LoaderProcessingBlockProps {
  /** WEB-1428: кастомная подпись «Пропустить»; пусто = словарь. */
  skipLabel?: string;
  /** WEB-672 2a: rich field (string | RichSpan[]); plain strings keep legacy render. */
  title?: RichField;
  /** WEB-672 2a: interpolation ctx for the title (legacy interp() parity). */
  varCtx?: VariableContext;
  /**
   * WEB-2923: дверь подстановки фасада (`ctx.interp` / `ctx.richText`) для
   * остальных текстов автора — стадий, соцпруфа, вопроса, вариантов и
   * «Пропустить». Ответ на вопрос пишется прежним текстом варианта.
   */
  interp: ExtendedRenderContext['interp'];
  richText: ExtendedRenderContext['richText'];
  steps?: LoaderStep[];
  textColor: string;
  /** WEB-682b: id блока — по завершении всех шагов пишем маркер 'done' в
      FormValues; плеер подхватывает его тем же эффектом, что single_choice
      autoAdvance. Без id сигнал не пишется (легаси-вызовы). */
  blockId?: string;
  /** WEB-682b: в editor-превью рисуем бейдж «→ авто-переход» (навигации нет). */
  autoAdvance?: boolean;
  /** WEB-925: подсекция loader активного стиль-пака; без пака — дефолт. */
  loaderStyle?: StylePackLoader;
  /**
   * Задание stylepack/loader-block-look: свой вид загрузчика блока (сырое
   * `block.loaderLook`, разбирает `resolveQuizLoader`). Сильнее поля воронки.
   */
  loaderLook?: unknown;
  /** WEB-925: тексты соцпруфа живут в блоке — пак задаёт только форму показа. */
  socialProof?: string[];
  /**
   * WEB-925 (гэп 1): вопросы-модалки. Показываются только если пак объявил
   * `loader.engagementModals`; сами тексты живут в блоке.
   */
  questions?: LoaderQuestion[];
  /**
   * WEB-1440 (запрос PM 18.08): вид попапа-вопроса отдельно от блока — фон
   * карточки модалки (цвет + плотность; блюр/стекло работают: элемент
   * стилизуется напрямую) и цвет текста внутри (заголовок, варианты,
   * «Пропустить»). Absent = как раньше.
   */
  modalBackground?: BlockBackground;
  modalTextColor?: string;
  /**
   * WEB-1699 п.2 (репорт владельца 24.08): заливка ВАРИАНТОВ ответа внутри
   * попапа. У самой модалки фон настраивался с WEB-1440, а до вариантов внутри
   * неё не дотянули — они оставались белыми прямоугольниками из переменных
   * темы. Absent = ровно прежний вид.
   */
  modalOptionBackground?: string;
  /**
   * WEB-1692 юнит 8: роль `Title` — заголовок экрана ожидания.
   * Пусто = сегодняшний вид (19px цветом блока).
   */
  titleRole?: TextRoleStyle;
  /**
   * WEB-1692 юнит 8: роль `Secondary` — ВСЁ, что служит при заголовке:
   * названия стадий, карточки с ответами и строка отзыва. Одной настройкой,
   * потому что это однородная обслуживающая мелочь одного экрана. Попап с
   * вопросом и процент сюда НЕ входят — у них свои поля (WEB-1440/1699).
   */
  secondaryRole?: TextRoleStyle;
}

/**
 * WEB-1692 юнит 8: кегли текстов роли `Secondary` сегодня разные — стадия 15,
 * карточка ответа 14, строка отзыва 13/14. Авторское число ставится на ГЛАВНЫЙ
 * текст группы (стадию), соседние считаются от него ТЕМ ЖЕ отношением, что
 * стоит в вёрстке сегодня (канон `scaledTextRoleStyle`, вариант А владельца
 * 02.09): иначе одно число схлопнуло бы иерархию экрана.
 */
const LOADER_STAGE_FONT_SIZE = 15;
const LOADER_SUMMARY_FONT_SIZE = 14;

/** WEB-925 (гэп 1): пустой список вопросов со стабильной ссылкой (deps эффекта). */
const NO_LOADER_QUESTIONS: LoaderQuestion[] = [];

/**
 * WEB-925 (гэп 1): страховка от тупика. Если пользователь не ответил и не
 * закрыл модалку, она снимается сама и лоадер доигрывает — воронка не может
 * зависнуть навсегда на модалке.
 */
const LOADER_MODAL_AUTO_CLOSE_MS = 15000;

/**
 * WEB-1012: шаг карусели соцпруфа. Карусель живёт своим таймером и НИКАК не
 * связана с расписанием стадий — приёмка отдельно требует, чтобы экран загрузки
 * не удлинялся ради показа отзывов (п.5).
 */
const LOADER_PROOF_ROTATE_MS = 3000;

/**
 * WEB-1699 (второй возврат 02.09): фон кнопки варианта в попапе, когда своей
 * заливки автор не задал, а вид попапа задал. Значение запекается в разметку и
 * ровно от него считается цвет надписи — см. `modalOptionOwnStyle` ниже.
 */
const LOADER_OPTION_DEFAULT_FILL = '#ffffff';

function LoaderProcessingBlock({
  title,
  varCtx,
  interp,
  richText,
  steps,
  textColor,
  blockId,
  autoAdvance,
  loaderStyle,
  loaderLook,
  socialProof,
  questions,
  skipLabel,
  modalBackground,
  modalTextColor,
  modalOptionBackground,
  titleRole,
  secondaryRole,
}: LoaderProcessingBlockProps): React.ReactElement {
  // WEB-1692 юнит 8. Пустые роли дают пустые накладки — разметка остаётся
  // байт-в-байт прежней.
  const roleTitle: TextRoleStyle = titleRole ?? {};
  const roleSecondary: TextRoleStyle = secondaryRole ?? {};
  const summaryRole = scaledTextRoleStyle(
    roleSecondary,
    LOADER_SUMMARY_FONT_SIZE / LOADER_STAGE_FONT_SIZE,
  );
  // WEB-785: locale-aware fallback (funnel language, via varCtx.system.locale)
  // — replaces the always-English DEFAULT_LOADER_TITLE/DEFAULT_LOADER_STEPS
  // when the AI-generated block omits title/steps.
  const localized = getPublicQuizStrings(varCtx?.system?.locale);
  // WEB-1012: воронка вокруг блока — источник имён стадий и отзывов. Вне визарда
  // (карточка блока, hover-превью) она пустая, и всё остаётся как было.
  const { screens: funnelScreens, quizLoader } = useFunnelContent();
  /**
   * WEB-1012: стадии по умолчанию строятся из секций воронки. Локализованный
   * зашитый список остаётся ЗАПАСНЫМ и заодно задаёт длительности — поэтому
   * экран загрузки не может стать длиннее прежнего.
   */
  const defaultSteps = useMemo<LoaderStep[]>(
    () => funnelSectionStages(funnelScreens, blockId, localized.loaderSteps),
    [funnelScreens, blockId, localized.loaderSteps],
  );
  const effectiveSteps = steps && steps.length > 0 ? steps : defaultSteps;
  const [doneCount, setDoneCount] = useState(0);
  /** WEB-925 (гэп 1): индекс открытого вопроса; null — модалки нет. */
  const [modalIndex, setModalIndex] = useState<number | null>(null);
  const { setValue } = useFormValues();
  const { isEditorPreview, frozen: previewFrozen } = useEditorPreview();
  // WEB-3673: на прогретой SDK странице (`?preload=1`) стадии и прокрутка
  // соцпруфа стоят до показа человеку — заставка не «проходит» в фоне.
  const shown = usePageShown();
  const frozen = previewFrozen || !shown;

  // WEB-682b: сигнал завершения — ровно один раз, когда все шаги отработали.
  const total = effectiveSteps.length;
  const completedRef = useRef(false);
  useEffect(() => {
    completedRef.current = false;
  }, [steps]);
  useEffect(() => {
    if (!blockId || completedRef.current) return;
    if (total > 0 && doneCount >= total) {
      completedRef.current = true;
      setValue(blockId, 'done');
    }
  }, [doneCount, total, blockId, setValue]);

  // WEB-925 (гэп 1): расписание стадий стало паузящимся. Пока модалка открыта,
  // таймеры оставшихся стадий сняты; при закрытии эффект перепланирует их с
  // текущего места. Без модалок (modalIndex всегда null) эффект отрабатывает
  // ровно один раз и планирует все стадии сразу — как до этого атома.
  const doneCountRef = useRef(0);
  doneCountRef.current = doneCount;
  const stepsRef = useRef<LoaderStep[] | undefined>(steps);
  const shownQuestionsRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    const list = steps && steps.length > 0 ? steps : defaultSteps;
    let startFrom = doneCountRef.current;
    if (stepsRef.current !== steps) {
      // Смена набора шагов — начинаем сначала (поведение до WEB-925).
      stepsRef.current = steps;
      startFrom = 0;
      doneCountRef.current = 0;
      shownQuestionsRef.current = new Set();
      setDoneCount(0);
      setModalIndex(null);
    }
    // WEB-2996: замороженный рендер (узел графа) — стадии не идут.
    if (modalIndex !== null || frozen) return undefined;
    const timers: ReturnType<typeof setTimeout>[] = [];
    let elapsed = 0;
    for (let index = startFrom; index < list.length; index += 1) {
      elapsed += Math.max(Number(list[index].durationMs) || 0, 0);
      const nextDone = index + 1;
      timers.push(setTimeout(() => setDoneCount(nextDone), elapsed));
    }
    return () => timers.forEach((timer) => clearTimeout(timer));
    // defaultSteps в deps не нужен: длительности он берёт из локализованного
    // запасного списка (константа локали), а смена имён секций расписание не
    // двигает — перепланировать стадии на переименовании экрана в конструкторе
    // значило бы дёргать лоадер под руками автора.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, modalIndex, frozen]);

  const progressPct = total > 0 ? Math.round((doneCount / total) * 100) : 100;

  // WEB-925: форма лоадера из пака. Дефолт равен сегодняшнему виду, поэтому
  // воронки без пака и с паком-`all-stages` рендерятся одинаково.
  // Слово владельца 21.10.2026: свои поля воронки settings.quizLoader
  // перебивают пак по каждому подполю; нет поля — ровно пак.
  // Задание stylepack/loader-block-look: поле самого блока (loaderLook)
  // сильнее поля воронки — та же дверь, третьим аргументом.
  const cfg = resolveQuizLoader(
    loaderStyle ?? DEFAULT_LOADER_STYLE,
    quizLoader,
    loaderLook,
  );
  const own = cfg.own;
  const loaderFill = own.color ?? LOADER_ACCENT;
  const loaderTrack = own.trackColor ?? LOADER_TRACK;
  // Задание stylepack/loader-title-position: подпись под индикатором. Зазоры
  // меняются местами: 16 px — между индикатором и подписью (как сейчас между
  // подписью и индикатором), 20 px — после подписи (как сейчас после
  // индикатора). Нет поля или `above` — разметка прежняя.
  const titleBelow = own.titlePosition === 'below';
  const indicatorGap = titleBelow ? 16 : 20;
  const showStageList =
    cfg.style === 'all-stages' || cfg.style === 'stacked-stages';
  // Стекающиеся стадии (12min): видны только пройденные и текущая.
  const visibleSteps =
    cfg.style === 'stacked-stages'
      ? effectiveSteps.slice(0, Math.min(doneCount + 1, total))
      : effectiveSteps;
  const summaryItems = cfg.answersSummary
    ? loaderAnswerSummaryItems(varCtx, 4, blockId)
    : [];
  const blockProof = (socialProof ?? []).filter((t) => t.trim() !== '');
  /**
   * WEB-1012: своих текстов у блока нет — форма «цитата отзыва» берёт УЖЕ
   * СУЩЕСТВУЮЩИЕ отзывы воронки, отдельной сущности не заводим. Форму «лавры»
   * так не наполняем осознанно: это награды (App of the Day / Trustpilot), а не
   * отзывы покупателей — цитата в чипе награды читалась бы враньём.
   */
  const funnelProof = useMemo(
    () =>
      cfg.socialProof === 'review' ? funnelReviewQuotes(funnelScreens) : [],
    [cfg.socialProof, funnelScreens],
  );
  const proofItems =
    cfg.socialProof === 'none'
      ? []
      : blockProof.length > 0
        ? blockProof
        : funnelProof;

  // WEB-1012: соцпруф ПРОКРУЧИВАЕТСЯ — на экране всегда одна строка, следующая
  // приходит своим таймером. Одна строка — крутить нечего, таймер не заводим.
  const proofCount = proofItems.length;
  const [proofIndex, setProofIndex] = useState(0);
  useEffect(() => {
    setProofIndex(0);
  }, [proofCount]);
  useEffect(() => {
    if (proofCount <= 1 || frozen) return undefined;
    const timer = setInterval(
      () => setProofIndex((current) => (current + 1) % proofCount),
      LOADER_PROOF_ROTATE_MS,
    );
    return () => clearInterval(timer);
  }, [proofCount, frozen]);
  const proofItem =
    proofCount > 0 ? proofItems[proofIndex % proofCount] : undefined;

  // WEB-925 (гэп 1): модалки-вопросы. Пак решает, показывать ли их вообще —
  // воронка без пака и паки с engagementModals=false работают как раньше.
  const engagementQuestions = cfg.engagementModals
    ? (questions ?? NO_LOADER_QUESTIONS)
    : NO_LOADER_QUESTIONS;
  useEffect(() => {
    if (engagementQuestions.length === 0) return;
    // Вопрос N привязан к границе стадии N. Последняя стадия исключена
    // намеренно: на ней срабатывает сигнал завершения, и модалка либо
    // задержала бы его, либо всплыла бы уже после ухода с экрана.
    if (doneCount <= 0 || doneCount >= total) return;
    const questionIndex = doneCount - 1;
    if (!engagementQuestions[questionIndex]) return;
    if (shownQuestionsRef.current.has(questionIndex)) return;
    shownQuestionsRef.current.add(questionIndex);
    setModalIndex(questionIndex);
  }, [doneCount, total, engagementQuestions]);

  // Страховка от тупика: молчание пользователя снимает модалку само.
  useEffect(() => {
    if (modalIndex === null) return undefined;
    const timer = setTimeout(
      () => setModalIndex(null),
      LOADER_MODAL_AUTO_CLOSE_MS,
    );
    return () => clearTimeout(timer);
  }, [modalIndex]);

  const activeQuestion =
    modalIndex === null ? undefined : engagementQuestions[modalIndex];
  const answerEngagementQuestion = (option?: string) => {
    if (modalIndex === null) return;
    // Ответ ложится под собственный ключ: values[blockId] занят маркером 'done'
    // и запись туда продвинула бы экран раньше времени.
    if (option !== undefined && blockId) {
      setValue(loaderQuestionValueKey(blockId, modalIndex), option);
    }
    setModalIndex(null);
  };

  // Доступность: фокус уезжает в модалку, чтобы Escape и Tab работали сразу.
  const modalRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (modalIndex !== null) modalRef.current?.focus();
  }, [modalIndex]);

  const stepStatus = (index: number): 'done' | 'active' | 'pending' => {
    if (index < doneCount) return 'done';
    if (index === doneCount) return 'active';
    return 'pending';
  };

  const indicatorStyle = (
    status: 'done' | 'active' | 'pending',
  ): React.CSSProperties => {
    const base: React.CSSProperties = {
      width: 20,
      height: 20,
      borderRadius: '50%',
      flexShrink: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
    };
    if (status === 'done') return { ...base, background: LOADER_ACCENT };
    if (status === 'active') {
      return {
        ...base,
        border: `2.5px solid ${LOADER_ACCENT}`,
        borderTopColor: 'transparent',
        animation: 'qb-loader-spin 0.8s linear infinite',
      };
    }
    return {
      ...base,
      border: '2px solid var(--color-border-primary, #d9d9d9)',
      opacity: 0.6,
    };
  };

  /**
   * «У попапа задан СВОЙ вид» (WEB-1440): автор выбрал цвет текста или
   * непустую заливку.
   *
   * Ревизия пачек 6-7: одно условие стояло двумя рукописными копиями — на
   * подложке окна и на самой карточке (переопределение
   * --color-text-on-surface). Копии обязаны совпадать буква в букву. Теперь
   * признак один; его два конца — переопределение переменной у карточки и
   * запечённая пара «фон ↔ текст» у кнопки варианта (см. ниже).
   */
  const modalHasOwnAppearance = Boolean(
    // WEB-3684: «непустая заливка» — по одной двери приёма поля фона.
    modalTextColor || toBlockBackground(modalBackground),
  );

  /**
   * WEB-1699, ВТОРОЙ возврат приёмки (круг 01.09, дорожка A7b) — пара «фон ↔
   * текст на нём» у кнопки варианта ЗАПЕКАЕТСЯ, а не наследуется.
   *
   * Дефект: «Фон попапа-вопроса» задан явно (#CC0000), «Заливка вариантов»
   * пуста — покупатель видит белый прямоугольник без надписи. Белое по белому.
   *
   * Почему первая починка (25.08) не удержала. Она оставила цвет надписи
   * ВЫВОДИМЫМ из наследуемой переменной: подложка окна снимала внешний
   * --color-text-on-surface в алиас, кнопка читала алиас. Пара держалась ровно
   * до тех пор, пока внешнее значение переменной оставалось посчитанным против
   * ФОНА КНОПКИ. Любой предок, который задаёт «текст на поверхности» отдельно
   * от самой поверхности, снимок отравляет — так умеет «Цвет текста внутри
   * элемента» (blockInnerSurface: свой цвет без своей заливки), и так же ведёт
   * себя любой будущий слой. Починка зависела от того, чего рендер кнопки не
   * контролирует, — поэтому она и развалилась.
   *
   * Лечение — снять саму зависимость: считаем контраст ровно от того фона,
   * который сами же и ставим кнопке. ОДИН расчёт на оба состояния:
   *   • автор задал заливку — фон её, текст посчитан по ней;
   *   • заливки нет — фон белый, запечённый явно, текст посчитан по белому.
   * Фон и цвет всегда пишутся ОДНИМ значением, разъехаться им больше нечем.
   *
   * Почему белый можно запекать (шрам WEB-1004 — «тёмная тема, тёмная
   * поверхность»): ветка включается ТОЛЬКО когда автор сам покрасил попап
   * (modalHasOwnAppearance). Тогда кнопка становится самостоятельной белой
   * плашкой с тёмной надписью — читаемой при любой теме. Воронка без своего
   * вида попапа в эту ветку не заходит вовсе: там кнопка по-прежнему берёт
   * поверхность темы вместе с её же «текстом на поверхности» (пара из одного
   * слоя, тёмная тема как была), и разметка прежняя до байта.
   *
   * Полупрозрачная заливка (#rrggbbaa с низкой альфой) контраст НЕ включает:
   * сквозь неё видна карточка окна, и яркость самой заливки не предсказывает,
   * что окажется под текстом. Порог берётся ОБЩИЙ — `opaqueContrastBaseOf`
   * (themes/surfaceOpacity), тот же, что у пилюли, кнопки, значка и «Баннера
   * срочности»; своего, третьего порога здесь нет: разъехавшиеся пороги это и
   * есть та карточка (WEB-1859). Раньше он звался через переходник
   * `opaqueHexOf` в captionChipAppearance — прыжок снят, поведение то же
   * (переходник состоит из одного этого вызова). Надпись при плёнке
   * остаётся на прежней цепочке переменных, то есть красится «текстом на
   * поверхности» карточки — того слоя, который сквозь заливку и просвечивает.
   */
  const modalOptionFill = modalOptionBackground?.trim() || undefined;
  const modalOptionPaintedFill =
    modalOptionFill ??
    (modalHasOwnAppearance ? LOADER_OPTION_DEFAULT_FILL : undefined);
  const modalOptionOpaqueFill = modalOptionPaintedFill
    ? opaqueContrastBaseOf(modalOptionPaintedFill)
    : null;
  const modalOptionOwnStyle: React.CSSProperties = modalOptionPaintedFill
    ? {
        background: modalOptionPaintedFill,
        ...(modalOptionOpaqueFill
          ? { color: pickOnPrimaryColor(modalOptionOpaqueFill) }
          : {}),
      }
    : {};

  const titleNode = (
    <h3
      style={{
        // WEB-1692 юнит 8: роль `Title` — только этот заголовок.
        fontSize: roleTitle.fontSize ?? 19,
        fontWeight: 700,
        color: roleTitle.color ?? textColor,
        margin: titleBelow ? '0 0 20px' : '0 0 16px',
        lineHeight: 1.3,
      }}
    >
      <RichTextContent
        value={title}
        varCtx={varCtx}
        fallback={localized.loaderTitle}
      />
    </h3>
  );

  return (
    <div
      style={{ textAlign: 'center', marginBottom: 16, position: 'relative' }}
    >
      <style>
        {'@keyframes qb-loader-spin { to { transform: rotate(360deg); } }'}
      </style>
      {isEditorPreview && autoAdvance && (
        // WEB-682b: в редакторе навигации нет — показываем, что на живой
        // воронке по завершении шагов случится авто-Next.
        <div
          style={{
            display: 'inline-block',
            marginBottom: 8,
            padding: '2px 10px',
            borderRadius: 'var(--border-radius-md, 10px)',
            fontSize: 11,
            background: 'var(--color-bg-tertiary, #f0f0f0)',
            color: 'var(--color-text-secondary, #8c8c8c)',
          }}
        >
          {pickStrings(authorHintsStrings).autoAdvanceBadge}
        </div>
      )}
      {!titleBelow && titleNode}
      {cfg.style === 'ring' ? (
        <LoaderRing
          percent={progressPct}
          accent={loaderFill}
          track={loaderTrack}
          size={own.ringSize}
          stroke={own.ringStroke}
          // У кольца процент подразумевается; снимает его только своё поле.
          showPercent={own.showPercent ?? true}
          gapBelow={indicatorGap}
        />
      ) : (
        <>
          {cfg.showPercent && (
            <div
              data-testid="loader-percent"
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: textColor,
                marginBottom: 8,
              }}
            >
              {progressPct}%
            </div>
          )}
          <div
            data-testid="loader-bar"
            style={{
              height: 6,
              borderRadius: 'var(--border-radius-sm, 3px)',
              background: loaderTrack,
              overflow: 'hidden',
              marginBottom: indicatorGap,
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${progressPct}%`,
                borderRadius: 'var(--border-radius-sm, 3px)',
                background: loaderFill,
                transition: 'width 0.4s ease',
              }}
            />
          </div>
        </>
      )}
      {titleBelow && titleNode}
      {summaryItems.length > 0 && (
        // WEB-925 (Astroline): карточки с ответами пользователя вместо стадий —
        // ожидание объясняется тем, что система читает именно его данные.
        <div
          data-testid="loader-answers-summary"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            justifyContent: 'center',
            marginBottom: 20,
          }}
        >
          {summaryItems.map((item, index) => (
            <span
              key={index}
              style={{
                padding: '8px 14px',
                borderRadius: 'var(--radius-card, 12px)',
                border: '1px solid var(--color-border-primary, #d9d9d9)',
                // WEB-1692 юнит 8: роль `Secondary` с сохранением пропорции —
                // карточка сегодня мельче стадии.
                fontSize: summaryRole.fontSize ?? LOADER_SUMMARY_FONT_SIZE,
                color: summaryRole.color ?? textColor,
              }}
            >
              {item}
            </span>
          ))}
        </div>
      )}
      {showStageList && (
        <div
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            gap: 12,
            textAlign: 'left',
            maxWidth: '100%',
          }}
        >
          {visibleSteps.map((step, index) => {
            const status = stepStatus(index);
            return (
              <div
                // WEB-809 wave 2a: label теперь RichField (массив спанов не
                // годится в шаблонную строку ключа) — index уникален сам.
                key={index}
                data-step-status={status}
                style={{ display: 'flex', alignItems: 'center', gap: 10 }}
              >
                <span style={indicatorStyle(status)}>
                  {status === 'done' && <LoaderCheckIcon />}
                </span>
                <span
                  style={{
                    // WEB-1692 юнит 8: роль `Secondary` — главный текст группы,
                    // авторское число ставится сюда как есть.
                    fontSize: roleSecondary.fontSize ?? LOADER_STAGE_FONT_SIZE,
                    lineHeight: 1.4,
                    fontWeight: status === 'active' ? 600 : 400,
                    // Будущая стадия приглушается АВТОРСКИМ цветом, а не своей
                    // переменной темы: иначе заданный цвет прочитался бы как
                    // «все стадии уже пройдены». Без авторского цвета всё
                    // остаётся ровно как сегодня.
                    color:
                      roleSecondary.color ??
                      (status === 'pending'
                        ? 'var(--color-text-secondary, #8c8c8c)'
                        : textColor),
                    ...(status === 'pending'
                      ? mutedTextRoleStyle(roleSecondary)
                      : {}),
                    transition: 'color 0.3s ease',
                  }}
                >
                  {/* WEB-809 wave 2a: rich-подпись шага; строки — как раньше. */}
                  {richText(step.label)}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {proofItem !== undefined && (
        // WEB-925 + WEB-1012: соцпруф в лоадере — лавры (BetterMe) либо цитата
        // отзыва (BetterMe №2), и то и другое КАРУСЕЛЬЮ: на экране одна строка,
        // следующая приходит сама. Данных нет — не рисуется ничего, поэтому
        // воронка без отзывов не получает пустого места (приёмка п.3).
        <div
          data-testid="loader-social-proof"
          data-social-proof={cfg.socialProof}
          data-proof-index={proofIndex % proofCount}
          data-proof-count={proofCount}
          aria-live="polite"
          style={{
            marginTop: 20,
            display: 'flex',
            gap: 8,
            justifyContent: 'center',
            // WEB-1692 юнит 8: та же роль `Secondary` с сохранением пропорции —
            // строка отзыва сегодня мельче стадии (13, у вида «отзыв» 14).
            fontSize:
              scaledTextRoleStyle(
                roleSecondary,
                (cfg.socialProof === 'review' ? 14 : 13) /
                  LOADER_STAGE_FONT_SIZE,
              ).fontSize ?? (cfg.socialProof === 'review' ? 14 : 13),
            fontStyle: cfg.socialProof === 'review' ? 'italic' : 'normal',
            color:
              roleSecondary.color ?? 'var(--color-text-secondary, #8c8c8c)',
          }}
        >
          {cfg.socialProof === 'laurels' ? (
            <span
              style={{
                padding: '6px 12px',
                borderRadius: 'var(--border-radius-md, 10px)',
                background: 'var(--color-bg-tertiary, #f0f0f0)',
              }}
            >
              {interp(proofItem)}
            </span>
          ) : (
            <p style={{ margin: 0, maxWidth: 420 }}>{interp(proofItem)}</p>
          )}
        </div>
      )}
      {activeQuestion && (
        // WEB-925 (гэп 1): вопрос поверх лоадера. Прогресс на паузе, пока
        // модалка открыта; закрыть можно ответом, «Пропустить» или Escape, а
        // если пользователь молчит — модалка снимается сама (см. таймер выше).
        <div
          data-testid="loader-engagement-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`loader-engagement-title-${modalIndex}`}
          tabIndex={-1}
          ref={modalRef}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              answerEngagementQuestion(undefined);
            }
          }}
          style={{
            position: isEditorPreview ? 'absolute' : 'fixed',
            inset: 0,
            zIndex: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            background: 'rgba(0, 0, 0, 0.45)',
            // WEB-1699 (второй возврат 02.09): снимка «текста на поверхности»
            // здесь БОЛЬШЕ НЕТ. Он был попыткой донести до кнопки варианта
            // значение переменной, посчитанное вне карточки, — и держался ровно
            // до первого предка, который задаёт «текст на поверхности» отдельно
            // от самой поверхности. Кнопка теперь не наследует цвет ни от кого:
            // фон и текст на нём запекаются одной парой (modalOptionOwnStyle).
          }}
        >
          <div
            data-testid="loader-engagement-card"
            style={{
              width: '100%',
              maxWidth: 360,
              padding: 20,
              borderRadius: 'var(--radius-card, 12px)',
              // WEB-1004: карточка — ПОВЕРХНОСТЬ (класс WEB-966/1000), а не
              // страничный фон; текст — on-surface, а не переданный textColor
              // (= АКЦЕНТ воронки: светлый акцент давал белое на белом,
              // скрин PM; палитрой из чата это не лечилось — дефект рендера).
              background: 'var(--color-bg-surface, #fff)',
              boxShadow: 'var(--shadow-medium, 0 4px 12px rgba(0, 0, 0, 0.12))',
              textAlign: 'center',
              // WEB-1440 (запрос PM 18.08): свой вид попапа поверх дефолта.
              // Фон — той же механикой, что значок гарантии
              // (badgeInnerBackgroundStyle: заливка + авто-контраст текста);
              // свой цвет текста сильнее авто-контраста. Дочерние узлы красятся
              // через var(--color-text-on-surface) — переопределяем её здесь же,
              // чтобы настройка доехала до заголовка, вариантов и «Пропустить».
              ...badgeInnerBackgroundStyle(modalBackground),
              ...(modalTextColor ? { color: modalTextColor } : {}),
              ...(modalHasOwnAppearance && {
                '--color-text-on-surface':
                  modalTextColor ||
                  (badgeInnerBackgroundStyle(modalBackground).color as string),
              }),
            }}
          >
            <p
              id={`loader-engagement-title-${modalIndex}`}
              style={{
                margin: '0 0 16px',
                fontSize: 17,
                fontWeight: 600,
                color: 'var(--color-text-on-surface, #1a1a1a)',
              }}
            >
              {interp(activeQuestion.question)}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {activeQuestion.options.map((option, index) => (
                <button
                  key={index}
                  type="button"
                  data-testid="loader-engagement-option"
                  onClick={() => answerEngagementQuestion(option)}
                  style={{
                    padding: '10px 14px',
                    borderRadius: 'var(--border-radius-md, 10px)',
                    border: '1px solid var(--color-border-primary, #d9d9d9)',
                    // WEB-1004: вариант — тоже поверхность (см. карточку выше).
                    // Это ПРЕЖНЯЯ пара из одного слоя темы: поверхность и
                    // «текст на поверхности», посчитанный против неё же
                    // (themes/index.ts). Воронка без своего вида попапа остаётся
                    // ровно на ней — включая тёмную тему (шрам WEB-1004).
                    background: 'var(--color-bg-surface, #fff)',
                    fontSize: 15,
                    color: 'var(--color-text-on-surface, #1a1a1a)',
                    cursor: 'pointer',
                    // WEB-1699 п.2 (репорт владельца 24.08) + ВТОРОЙ возврат
                    // приёмки 02.09: своя заливка варианта и запечённая пара
                    // «фон ↔ текст на нём». Накладка идёт ПОСЛЕ дефолтов, то
                    // есть перекрывает их; пустая накладка (вид попапа не
                    // задан) оставляет ветку прежней. Как считается — см.
                    // modalOptionOwnStyle: один расчёт на оба состояния, по
                    // тому фону, который тут же и ставится.
                    //
                    // Отдельного поля «цвет текста варианта» намеренно НЕ
                    // заводим: оно вернуло бы возможность сделать нечитаемо, а
                    // выигрыш — только у редкого случая «хочу свой цвет вопреки
                    // контрасту».
                    ...modalOptionOwnStyle,
                  }}
                >
                  {interp(option)}
                </button>
              ))}
            </div>
            <button
              type="button"
              data-testid="loader-engagement-skip"
              onClick={() => answerEngagementQuestion(undefined)}
              style={{
                // Скрин PM 18.08 (класс WEB-966/WEB-1440): кнопка лежит на
                // карточке модалки — цвет обязан идти через on-surface, как у
                // заголовка и вариантов выше (WEB-1004); secondary — цвет
                // текста ЭКРАНА, на тёмной воронке он светлый → белым по
                // белой карточке. Цвет — первым в стиле: сторож WEB-1440
                // ищет его в 13 строках от data-testid.
                color:
                  'var(--color-text-on-surface, var(--color-text-secondary, #8c8c8c))',
                marginTop: 12,
                border: 'none',
                background: 'transparent',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {interp(skipLabel).trim() ||
                getPublicQuizStrings(varCtx?.system?.locale).loaderSkipLabel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Ветка `loader_processing` фасада — тело `case` слово в слово. */
export function renderLoaderProcessing(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, varCtx, primaryColor, stylePackId, interp, richText } = ctx;
  // WEB-834 (инвентарь рассинхронов): генерация пишет short-имена
  // title/steps (BE-схема), редактор — loaderTitle/loaderSteps; читать
  // обязаны ОБА, иначе AI-заголовок и шаги молча падают в дефолт.
  const loaderAi = aiFields(block) as {
    title?: string;
    steps?: LoaderStep[];
    socialProof?: string[];
    questions?: LoaderQuestion[];
    skipLabel?: string;
  };
  return (
    <LoaderProcessingBlock
      skipLabel={block.loaderSkipLabel ?? loaderAi.skipLabel}
      modalBackground={block.loaderModalBackground}
      modalTextColor={block.loaderModalTextColor}
      modalOptionBackground={block.loaderModalOptionBackground}
      title={asRichField(block.loaderTitle ?? loaderAi.title)}
      varCtx={varCtx}
      interp={interp}
      richText={richText}
      steps={block.loaderSteps ?? loaderAi.steps}
      textColor={primaryColor}
      blockId={block.id}
      // WEB-1692 юнит 8: роли текста читаются ЗДЕСЬ, в ветке своего типа
      // — того же требует контракт имён полей (WEB-881), что и от
      // остальных полей блока.
      titleRole={blockTextRoles(block, 'loader_processing').title}
      secondaryRole={blockTextRoles(block, 'loader_processing').secondary}
      // WEB-925: форма лоадера из активного стиль-пака. Неизвестный id даёт
      // undefined → компонент падает в дефолт, равный сегодняшнему виду.
      loaderStyle={(() => {
        // WEB-1012 (решение владельца 11.08): форма — из пака (теперь обе
        // возможности включены у всех), но БЛОК может выключить каждую
        // своим переключателем в панели свойств. undefined = включено.
        const packLoader = stylePackId
          ? getStylePackById(stylePackId)?.loader
          : undefined;
        const base = packLoader ?? DEFAULT_LOADER_STYLE;
        return {
          ...base,
          socialProof:
            block.showLoaderSocialProof === false ? 'none' : base.socialProof,
          engagementModals:
            block.showLoaderQuestions === false ? false : base.engagementModals,
        };
      })()}
      // Задание stylepack/loader-block-look: свой вид загрузчика блока.
      loaderLook={block.loaderLook}
      socialProof={block.loaderSocialProof ?? loaderAi.socialProof}
      // WEB-925 (гэп 1): редактор пишет loaderQuestions, генерация —
      // короткое questions; читаем ОБА (класс field-desync WEB-780/605).
      questions={block.loaderQuestions ?? loaderAi.questions}
      autoAdvance={
        // WEB-682 ретест: редактор пишет loaderAutoAdvance, AI — autoAdvance.
        (block.loaderAutoAdvance ?? block.autoAdvance) === true
      }
    />
  );
}
